import React, { ReactElement, useEffect, useState, FocusEvent } from "react";

import styled from "styled-components";
import { useCombobox } from "downshift";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import Input from "../input";
import { ToggleTipProps } from "../../layout/toggle_tip";
import usePrevious from "../../../shared/use_previous";

const InputAutocompleteWrapper = styled.span`
  display: inline-block;
  width: 100%;
  .wrapper {
    width: 100%;
    position: relative;
  }

  .text-input {
    margin-bottom: ${({ theme }) => theme.deprecatedSpacing.component.five};
  }
`;

const StyledOptions = styled.ul<{ isOpen: boolean; maxHeight?: number }>`
  overflow: ${({ maxHeight }) => (maxHeight ? "scroll" : "hidden")};
  max-height: ${({ maxHeight }) => (maxHeight ? maxHeight : "")};
  background-color: ${({ theme }) => theme.deprecatedColors.primary.extraLight};
  border-radius: ${({ theme }) => theme.deprecatedBorder.radius.large};
  box-shadow: ${({ theme }) => theme.deprecatedElevation.two};
  padding: ${({ theme, isOpen }) => (isOpen ? theme.deprecatedSpacing.component.five : 0)};
  position: absolute;
  left: 0;
  right: 0;
  top: 90%;
  list-style-type: none;
  z-index: 1;
`;

const StyledOption = styled.li<{ isHighlighted: boolean; isDisabled?: boolean }>`
  border-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
  padding: ${({ theme }) => theme.deprecatedSpacing.component.four};
  background-color: ${({ theme, isHighlighted }) =>
    isHighlighted ? theme.deprecatedColors.primary.normal : "transparent"};
  color: ${({ theme, isHighlighted, isDisabled }) =>
    isDisabled
      ? theme.deprecatedColors.gray.seven
      : isHighlighted
      ? theme.deprecatedColors.white
      : theme.deprecatedColors.text.normal};
  cursor: ${({ isHighlighted, isDisabled }) =>
    isDisabled ? "not-allowed" : isHighlighted ? "pointer" : "normal"};
`;

//The label is optional when passed down as a prop
export interface AutocompleteItemType {
  value: string;
  label?: string;
  icon?: React.ReactNode;
}

//The label is required for the items used to generate the dropdown options
export interface DropdownItemType {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface InputAutocompleteProps {
  label?: string;
  helperText?: string;
  className?: string;
  error?: string;
  description?: string;
  toggleTip?: ReactElement<ToggleTipProps>;
  adornmentIcon?: IconProp;
  availableOptions: AutocompleteItemType[];
  id?: string;
  placeholder?: string;
  value?: string;
  onSelect: (value: string) => void;
  onChange?: (value: string | undefined) => void;
  defaultIsOpen?: boolean;
  clearOnSelect?: boolean;
  optional?: boolean;
  maxHeight?: number;
  onMenuScrollToBottom?: () => void;
  onBlur?: (e: FocusEvent) => void;
}

export default function InputAutocomplete({
  onSelect,
  label,
  helperText,
  className,
  error,
  toggleTip,
  adornmentIcon,
  description,
  placeholder,
  value,
  id,
  availableOptions,
  onChange,
  defaultIsOpen = false,
  clearOnSelect = true,
  optional = false,
  maxHeight = undefined,
  onMenuScrollToBottom = undefined,
  onBlur,
}: InputAutocompleteProps) {
  const formattedOptions: DropdownItemType[] = availableOptions.map((availableOption) => ({
    label: availableOption?.label ?? availableOption.value,
    value: availableOption.value,
    icon: availableOption?.icon ?? undefined,
  }));
  const [inputItems, setInputItems] = useState<DropdownItemType[]>(formattedOptions);
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
    openMenu,
    closeMenu,
  } = useCombobox({
    items: inputItems,
    itemToString: (item) => (item ? item.label : ""),
    onSelectedItemChange: ({ selectedItem }) => {
      if (clearOnSelect) setInputValue("");

      if (selectedItem && onSelect) {
        onSelect(selectedItem.value);
      }

      closeMenu();
    },
    onInputValueChange: ({ inputValue }) => {
      //Filters the options that are displayed in the dropdown menu
      if (onChange) onChange(inputValue);
      const itemsToSet = formattedOptions.filter((option) => {
        return option.label.toLowerCase().includes(inputValue?.toLowerCase() ?? "");
      });
      setInputItems([...itemsToSet]);
    },
    defaultIsOpen,
  });

  const prevOptions = usePrevious(availableOptions);
  useEffect(() => {
    // if the options passed in via props change, reset the dropdown
    // A simple !== will give us false positives
    if (!optionsMatch(prevOptions, availableOptions)) {
      setInputItems(formattedOptions);
    }
  }, [prevOptions, formattedOptions, availableOptions]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOptionsScroll = (event: { currentTarget: any }) => {
    const node = event.currentTarget;
    const isAtBottom = node?.scrollHeight - node?.scrollTop === node?.clientHeight;

    if (isAtBottom && !!onMenuScrollToBottom) {
      onMenuScrollToBottom();
    }
  };

  useEffect(() => {
    if (!value) {
      // if the value is invalidated by the parent, clear the input
      setInputValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <InputAutocompleteWrapper className={className}>
      <div className="wrapper">
        <Input
          {...getInputProps()}
          data-test="dropdown-options"
          type={"text"}
          error={error}
          adornmentIcon={adornmentIcon}
          toggleTip={toggleTip}
          helperText={helperText}
          label={label}
          labelProps={getLabelProps()}
          placeholder={placeholder}
          id={id}
          className={"text-input"}
          onFocus={openMenu}
          onClick={openMenu}
          onMouseDown={openMenu}
          description={description}
          questionIsOptional={optional}
          onBlur={onBlur}
        />

        <StyledOptions
          {...getMenuProps()}
          onScroll={handleOptionsScroll}
          isOpen={isOpen}
          className="input-options"
          maxHeight={maxHeight}
        >
          {isOpen &&
            inputItems.map((item, index) => (
              <StyledOption
                isHighlighted={highlightedIndex === index}
                key={`${item}${index}`}
                {...getItemProps({ item, index })}
              >
                {item?.icon || null}
                {item.label}
              </StyledOption>
            ))}
        </StyledOptions>
      </div>
    </InputAutocompleteWrapper>
  );
}

function optionsMatch(
  previous: AutocompleteItemType[] | undefined,
  current: AutocompleteItemType[],
): boolean {
  if (!previous || previous.length !== current.length) return false;
  return previous.every((option, index) => {
    const currentOption = current[index];
    return option.value === currentOption.value && option.label === currentOption.label;
  });
}
